import {
  WORKSPACE_SCHEMA_VERSION,
  type WorkspaceCardLayoutItem,
  type WorkspaceCardLayouts,
  type WorkspaceLayoutInput,
  type WorkspaceLayoutPreference,
} from '../../services/api/workspace';

/** Cards that ship with the workspace and are declared in the registry below. */
export type WorkspaceStaticCardId =
  | 'insights'
  | 'expected-arrivals'
  | 'radar'
  | 'collaboration'
  | 'inventory-health'
  | 'recent-sales-orders'
  | 'recent-purchase-orders'
  | 'purchase-orders-at-risk'
  | 'shipment-flow'
  | 'shipment-calendar'
  | 'warehouse-capacity'
  | 'warehouse-task-aging'
  | 'invoice-inbox'
  | 'invoice-cash'
  | 'invoice-payments';

/**
 * An analytics chart pinned to the workspace, keyed by its saved visual id.
 *
 * These are user-chosen rather than declared, so they have no registry entry —
 * `chartCardDefinition()` synthesises one. The `chart:` prefix keeps them
 * distinguishable from static ids and satisfies the server's card-id pattern
 * (`/^[a-z0-9][a-z0-9:_-]{0,127}$/i`), so no backend change is needed.
 */
export type WorkspaceChartCardId = `chart:${string}`;
export type WorkspaceDataCardId = `card:${string}`;

export type WorkspaceCardId = WorkspaceStaticCardId | WorkspaceChartCardId | WorkspaceDataCardId;

export const isChartCardId = (id: string): id is WorkspaceChartCardId => id.startsWith('chart:');
export const isVisualCardId = (id: string): id is WorkspaceChartCardId | WorkspaceDataCardId =>
  isChartCardId(id) || id.startsWith('card:');

/** The saved visual id behind a chart card. */
export const visualIdFromCardId = (id: WorkspaceChartCardId | WorkspaceDataCardId): string => id.slice(id.indexOf(':') + 1);

export const chartCardIdFor = (visualId: string): WorkspaceChartCardId => `chart:${visualId}`;

/**
 * Chart cards are not in the static height table. They default to the same
 * footprint as the taller built-in cards — a chart squeezed into a short card
 * is unreadable, which defeats the point of pinning it.
 */
const CHART_CARD_HEIGHT = 7;
const CHART_CARD_SPAN = 3;
const LEGACY_ARRIVAL_CARD: WorkspaceChartCardId = 'chart:system:visual:expected-arrival-timeline';
const ANALYTICS_ARRIVAL_CARD: WorkspaceChartCardId = 'chart:system:visual:expected-po-arrivals-calendar';
const ARRIVAL_CALENDAR_CARD: WorkspaceStaticCardId = 'expected-arrivals';

/** Registry entry for a chart card, which has none of its own. */
export const chartCardDefinition = (
  id: WorkspaceChartCardId | WorkspaceDataCardId,
  title = 'Chart'
): WorkspaceCardDefinition => ({
  id,
  title,
  module: 'Analytics',
  moduleHref: '/analytics',
  permissions: ['analytics:dashboard:read'],
  defaultSpan: CHART_CARD_SPAN,
  anchorLabel: title,
});

export type WorkspaceCardSpan = 2 | 3 | 4 | 6;
export type WorkspaceBreakpoint = keyof WorkspaceCardLayouts;

export const WORKSPACE_GRID_COLUMNS: Record<WorkspaceBreakpoint, number> = {
  lg: 6,
  md: 2,
  sm: 1,
};

export interface WorkspaceCardDefinition {
  id: WorkspaceCardId;
  title: string;
  module: string;
  /** Where the card's module label navigates to. */
  moduleHref: string;
  requiredPlugin?: string;
  permissions: string[];
  defaultSpan: WorkspaceCardSpan;
  anchorLabel: string;
}

/** The existing insights briefing stays visible, with its size saved alongside other cards. */
export const WORKSPACE_INSIGHTS_CARD: WorkspaceCardDefinition = {
  id: 'insights',
  title: 'LognAI Insights',
  module: 'LognAI',
  moduleHref: '/insights',
  permissions: [],
  defaultSpan: 6,
  anchorLabel: 'Insights',
};

export const WORKSPACE_CARD_REGISTRY: readonly WorkspaceCardDefinition[] = [
  {
    id: 'expected-arrivals',
    title: 'Expected Arrivals',
    module: 'Orders',
    moduleHref: '/orders/purchase',
    requiredPlugin: 'orders',
    permissions: ['order:purchase:read'],
    defaultSpan: 6,
    anchorLabel: 'Arrivals',
  },
  {
    id: 'radar',
    title: 'Radar',
    module: 'Operations',
    moduleHref: '/shipments/radar',
    permissions: [],
    defaultSpan: 3,
    anchorLabel: 'Radar',
  },
  {
    id: 'collaboration',
    title: 'My Tasks',
    module: 'Collaboration',
    moduleHref: '/tasks',
    requiredPlugin: 'tasks',
    permissions: ['tasks:task:read'],
    defaultSpan: 3,
    anchorLabel: 'Tasks',
  },
  {
    id: 'inventory-health',
    title: 'Inventory Health',
    module: 'Inventory',
    moduleHref: '/inventory/items',
    requiredPlugin: 'inventory',
    permissions: ['inventory:item:read'],
    defaultSpan: 3,
    anchorLabel: 'Inventory',
  },
  {
    id: 'recent-sales-orders',
    title: 'Orders Needing Attention',
    module: 'Orders',
    moduleHref: '/orders/sales',
    requiredPlugin: 'orders',
    permissions: ['order:sales:read'],
    defaultSpan: 3,
    anchorLabel: 'Orders',
  },
  {
    id: 'recent-purchase-orders',
    title: 'Recent Purchase Orders',
    module: 'Orders',
    moduleHref: '/orders/purchase',
    requiredPlugin: 'orders',
    permissions: ['order:purchase:read'],
    defaultSpan: 3,
    anchorLabel: 'Purchasing',
  },
  {
    id: 'purchase-orders-at-risk',
    title: 'Purchase Orders at Risk',
    module: 'Orders',
    moduleHref: '/orders/purchase',
    requiredPlugin: 'orders',
    permissions: ['order:purchase:read'],
    defaultSpan: 3,
    anchorLabel: 'PO risk',
  },
  {
    id: 'shipment-flow',
    title: 'Shipment Attention',
    module: 'Shipments',
    moduleHref: '/shipments',
    requiredPlugin: 'shipments',
    permissions: ['shipment:shipment:read'],
    defaultSpan: 3,
    anchorLabel: 'Shipments',
  },
  {
    id: 'shipment-calendar',
    title: 'Shipment Calendar',
    module: 'Shipments',
    moduleHref: '/shipments',
    requiredPlugin: 'shipments',
    permissions: ['shipment:shipment:read'],
    defaultSpan: 6,
    anchorLabel: 'Shipment calendar',
  },
  {
    id: 'warehouse-capacity',
    title: 'Warehouse Capacity',
    module: 'Warehouse',
    moduleHref: '/warehouse/warehouses',
    requiredPlugin: 'warehouse',
    permissions: ['warehouse:warehouse:read'],
    defaultSpan: 3,
    anchorLabel: 'Warehouse',
  },
  {
    id: 'warehouse-task-aging',
    title: 'Warehouse Task Aging',
    module: 'Warehouse',
    moduleHref: '/warehouse/tasks',
    requiredPlugin: 'warehouse',
    permissions: ['warehouse:task:read'],
    defaultSpan: 3,
    anchorLabel: 'Task aging',
  },
  {
    id: 'invoice-inbox',
    title: 'Invoice Inbox',
    module: 'Invoices',
    moduleHref: '/invoices/incoming',
    requiredPlugin: 'invoices',
    permissions: ['invoices:invoice:read'],
    defaultSpan: 3,
    anchorLabel: 'Invoice inbox',
  },
  {
    id: 'invoice-cash',
    title: 'Due Cash',
    module: 'Invoices',
    moduleHref: '/invoices',
    requiredPlugin: 'invoices',
    permissions: ['invoices:invoice:read'],
    defaultSpan: 3,
    anchorLabel: 'Due cash',
  },
  {
    id: 'invoice-payments',
    title: 'Payment Reconciliation',
    module: 'Invoices',
    moduleHref: '/invoices/payments',
    requiredPlugin: 'invoices',
    permissions: ['invoices:payment:read'],
    defaultSpan: 3,
    anchorLabel: 'Payment reconciliation',
  },
] as const;

export interface WorkspaceLayoutState {
  orderedCardIds: WorkspaceCardId[];
  orderedKpiIds: string[];
  pinnedCardIds: WorkspaceCardId[];
  cardLayouts: WorkspaceCardLayouts;
}

const DEFAULT_HEIGHTS: Record<WorkspaceStaticCardId, number> = {
  insights: 7,
  'expected-arrivals': 12,
  radar: 7,
  collaboration: 7,
  'inventory-health': 7,
  'recent-sales-orders': 7,
  'recent-purchase-orders': 7,
  'purchase-orders-at-risk': 7,
  'shipment-flow': 7,
  'shipment-calendar': 11,
  'warehouse-capacity': 7,
  'warehouse-task-aging': 7,
  'invoice-inbox': 7,
  'invoice-cash': 7,
  'invoice-payments': 7,
};

function defaultHeight(cardId: WorkspaceCardId): number {
  if (cardId === LEGACY_ARRIVAL_CARD || cardId === ANALYTICS_ARRIVAL_CARD) return 8;
  return isVisualCardId(cardId)
    ? CHART_CARD_HEIGHT
    : (DEFAULT_HEIGHTS[cardId as WorkspaceStaticCardId] ?? CHART_CARD_HEIGHT);
}

function defaultWidth(
  cardId: WorkspaceCardId,
  breakpoint: WorkspaceBreakpoint
): number {
  if (breakpoint === 'sm') return 1;
  if (cardId === 'shipment-calendar') return WORKSPACE_GRID_COLUMNS[breakpoint];
  if (cardId === 'insights') return WORKSPACE_GRID_COLUMNS[breakpoint];
  if (cardId === LEGACY_ARRIVAL_CARD || cardId === ARRIVAL_CALENDAR_CARD) {
    return WORKSPACE_GRID_COLUMNS[breakpoint];
  }
  if (breakpoint === 'md') return 1;
  // Uniform starting dimensions; saved geometry and manual resizing win.
  return CHART_CARD_SPAN;
}

function packWorkspaceLayout(
  orderedCardIds: WorkspaceCardId[],
  breakpoint: WorkspaceBreakpoint,
  source?: WorkspaceCardLayoutItem[]
): WorkspaceCardLayoutItem[] {
  const columns = WORKSPACE_GRID_COLUMNS[breakpoint];
  const sourceById = new Map(source?.map((item) => [item.i, item]));
  const packed: WorkspaceCardLayoutItem[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  for (const id of orderedCardIds) {
    const sourceItem = sourceById.get(id);
    const width = Math.max(
      1,
      Math.min(columns, Math.round(sourceItem?.w ?? defaultWidth(id, breakpoint)))
    );
    const sourceHeight = sourceItem?.h ?? defaultHeight(id);
    const height = Math.max(id === 'insights' ? 1 : 4, Math.min(20,
      id === 'insights' ? sourceHeight : Math.round(sourceHeight)));

    if (x + width > columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }

    packed.push({ i: id, x, y, w: width, h: height });
    x += width;
    rowHeight = Math.max(rowHeight, height);
    if (x === columns) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }
  }

  return packed;
}

export function createDefaultWorkspaceCardLayouts(
  orderedCardIds: WorkspaceCardId[]
): WorkspaceCardLayouts {
  return {
    lg: packWorkspaceLayout(orderedCardIds, 'lg'),
    md: packWorkspaceLayout(orderedCardIds, 'md'),
    sm: packWorkspaceLayout(orderedCardIds, 'sm'),
  };
}

function reconcileBreakpointLayout(
  savedItems: WorkspaceCardLayoutItem[] | undefined,
  availableCardIds: WorkspaceCardId[],
  breakpoint: WorkspaceBreakpoint
): WorkspaceCardLayoutItem[] {
  if (!savedItems?.length) return packWorkspaceLayout(availableCardIds, breakpoint);

  const available = new Set(availableCardIds);
  const columns = WORKSPACE_GRID_COLUMNS[breakpoint];
  const seen = new Set<WorkspaceCardId>();
  const retained: WorkspaceCardLayoutItem[] = [];

  for (const item of savedItems) {
    const id = item.i as WorkspaceCardId;
    if (!available.has(id) || seen.has(id)) continue;
    const width = Math.max(1, Math.min(columns, Math.round(item.w)));
    retained.push({
      i: id,
      x: Math.max(0, Math.min(columns - width, Math.round(item.x))),
      y: Math.max(0, item.y),
      w: width,
      h: Math.max(id === 'insights' ? 1 : 4, Math.min(20,
        id === 'insights' ? item.h : Math.round(item.h))),
    });
    seen.add(id);
  }

  if (available.has('insights') && !seen.has('insights')) {
    const [insights] = packWorkspaceLayout(['insights'], breakpoint);
    for (const item of retained) item.y += insights.h;
    retained.unshift(insights);
    seen.add('insights');
  }

  const missing = availableCardIds.filter((id) => !seen.has(id));
  if (!missing.length) return retained;
  const yOffset = retained.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0);
  return [
    ...retained,
    ...packWorkspaceLayout(missing, breakpoint).map((item) => ({
      ...item,
      y: item.y + yOffset,
    })),
  ];
}

export function reconcileWorkspaceLayout(
  saved:
    | Pick<
        WorkspaceLayoutPreference,
        'orderedCardIds' | 'pinnedCardIds' | 'cardLayouts' | 'orderedKpiIds'
      >
    | null
    | undefined,
  availableCardIds: WorkspaceCardId[],
  availableKpiIds: string[] = [],
  defaultKpiIds: readonly string[] = [],
  defaultCardIds: readonly WorkspaceCardId[] = availableCardIds
): WorkspaceLayoutState {
  const available = new Set(availableCardIds);
  const seen = new Set<WorkspaceCardId>();
  const ordered: WorkspaceCardId[] = [];
  // Replace the former workspace arrival chart in place, including its pins
  // and resized geometry. Omitted charts stay omitted. If both were explicitly
  // selected, retain them as separate Analytics charts.
  const replaceArrivalTimeline = available.has(ARRIVAL_CALENDAR_CARD) &&
    !saved?.orderedCardIds.includes(ARRIVAL_CALENDAR_CARD);
  const currentCardId = (id: string): WorkspaceCardId =>
    replaceArrivalTimeline && (id === LEGACY_ARRIVAL_CARD || id === ANALYTICS_ARRIVAL_CARD)
      ? ARRIVAL_CALENDAR_CARD : id as WorkspaceCardId;
  const currentLayoutItems = (items: WorkspaceCardLayoutItem[] | undefined) =>
    items?.map((item) => ({ ...item, i: currentCardId(item.i) }));

  // Insights used to sit outside the grid and has always been visible. Add it
  // to older preferences without restoring any intentionally removed cards.
  if (available.has('insights') && !saved?.orderedCardIds.includes('insights')) {
    seen.add('insights');
    ordered.push('insights');
  }

  // A saved order is the user's selection, including an intentionally empty
  // board. Only a workspace without preferences starts with every available card.
  for (const rawId of saved?.orderedCardIds ?? defaultCardIds) {
    const id = currentCardId(rawId);
    if (!available.has(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }

  const pinnedSet = new Set<WorkspaceCardId>();
  for (const rawId of saved?.pinnedCardIds ?? []) {
    const id = currentCardId(rawId);
    if (seen.has(id)) pinnedSet.add(id);
  }

  const orderedCardIds = [
    ...ordered.filter((id) => pinnedSet.has(id)),
    ...ordered.filter((id) => !pinnedSet.has(id)),
  ];

  return {
    orderedCardIds,
    orderedKpiIds: [...new Set(saved?.orderedKpiIds ?? defaultKpiIds)].filter((id) =>
      availableKpiIds.includes(id)
    ),
    pinnedCardIds: ordered.filter((id) => pinnedSet.has(id)),
    cardLayouts: {
      lg: reconcileBreakpointLayout(currentLayoutItems(saved?.cardLayouts?.lg), orderedCardIds, 'lg'),
      md: reconcileBreakpointLayout(currentLayoutItems(saved?.cardLayouts?.md), orderedCardIds, 'md'),
      sm: reconcileBreakpointLayout(currentLayoutItems(saved?.cardLayouts?.sm), orderedCardIds, 'sm'),
    },
  };
}

export function serializeWorkspaceLayout(state: WorkspaceLayoutState): WorkspaceLayoutInput {
  return {
    schemaVersion: WORKSPACE_SCHEMA_VERSION,
    orderedCardIds: state.orderedCardIds,
    orderedKpiIds: state.orderedKpiIds,
    pinnedCardIds: state.pinnedCardIds,
    cardLayouts: state.cardLayouts,
  };
}

export function workspaceLayoutsEqual(
  left: WorkspaceLayoutState,
  right: WorkspaceLayoutState
): boolean {
  return (
    left.orderedCardIds.join('|') === right.orderedCardIds.join('|') &&
    left.orderedKpiIds.join('|') === right.orderedKpiIds.join('|') &&
    left.pinnedCardIds.join('|') === right.pinnedCardIds.join('|') &&
    JSON.stringify(left.cardLayouts) === JSON.stringify(right.cardLayouts)
  );
}

function reflowWorkspaceLayouts(
  cardLayouts: WorkspaceCardLayouts,
  orderedCardIds: WorkspaceCardId[]
): WorkspaceCardLayouts {
  return {
    lg: packWorkspaceLayout(orderedCardIds, 'lg', cardLayouts.lg),
    md: packWorkspaceLayout(orderedCardIds, 'md', cardLayouts.md),
    sm: packWorkspaceLayout(orderedCardIds, 'sm', cardLayouts.sm),
  };
}

export function moveWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId,
  targetIndex: number
): WorkspaceLayoutState {
  const fromIndex = state.orderedCardIds.indexOf(cardId);
  if (fromIndex < 0) return state;

  const next = [...state.orderedCardIds];
  next.splice(fromIndex, 1);
  next.splice(Math.max(0, Math.min(targetIndex, next.length)), 0, cardId);

  return {
    ...state,
    orderedCardIds: next,
    pinnedCardIds: state.pinnedCardIds.filter((id) => next.includes(id)),
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, next),
  };
}

/**
 * Adds a card to the FRONT of the board (after any pinned cards).
 *
 * Appending would drop a just-added card at the bottom of a long grid, where
 * the user cannot see the result of their own action.
 */
export function addWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId,
  targetIndex?: number
): WorkspaceLayoutState {
  if (state.orderedCardIds.includes(cardId)) return state;
  const pinned = state.orderedCardIds.filter((id) => state.pinnedCardIds.includes(id));
  const rest = state.orderedCardIds.filter((id) => !state.pinnedCardIds.includes(id));
  const orderedCardIds = [...pinned, ...rest];
  const index = Math.max(0, Math.min(targetIndex ?? pinned.length, orderedCardIds.length));
  orderedCardIds.splice(index, 0, cardId);
  const pinnedCardIds = index < pinned.length ? orderedCardIds.filter((id) => id === cardId || pinned.includes(id)) : state.pinnedCardIds;
  return {
    ...state,
    orderedCardIds,
    pinnedCardIds,
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, orderedCardIds),
  };
}

/** Drops a card from the order, the pins, and every breakpoint's layout. */
export function removeWorkspaceCard(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId
): WorkspaceLayoutState {
  if (!state.orderedCardIds.includes(cardId)) return state;
  const orderedCardIds = state.orderedCardIds.filter((id) => id !== cardId);
  const drop = (items: WorkspaceCardLayoutItem[]) => items.filter((item) => item.i !== cardId);
  return {
    ...state,
    orderedCardIds,
    pinnedCardIds: state.pinnedCardIds.filter((id) => id !== cardId),
    cardLayouts: reflowWorkspaceLayouts(
      {
        lg: drop(state.cardLayouts.lg),
        md: drop(state.cardLayouts.md),
        sm: drop(state.cardLayouts.sm),
      },
      orderedCardIds
    ),
  };
}

export function toggleWorkspaceCardPin(
  state: WorkspaceLayoutState,
  cardId: WorkspaceCardId
): WorkspaceLayoutState {
  const isPinned = state.pinnedCardIds.includes(cardId);
  const pinnedCardIds = isPinned
    ? state.pinnedCardIds.filter((id) => id !== cardId)
    : [...state.pinnedCardIds, cardId];
  const pinnedSet = new Set(pinnedCardIds);
  const orderedCardIds = [
    ...state.orderedCardIds.filter((id) => pinnedSet.has(id)),
    ...state.orderedCardIds.filter((id) => !pinnedSet.has(id)),
  ];
  return {
    ...state,
    orderedCardIds,
    pinnedCardIds: orderedCardIds.filter((id) => pinnedSet.has(id)),
    cardLayouts: reflowWorkspaceLayouts(state.cardLayouts, orderedCardIds),
  };
}

export function applyWorkspaceGridLayouts(
  state: WorkspaceLayoutState,
  layouts: Partial<Record<WorkspaceBreakpoint, readonly WorkspaceCardLayoutItem[]>>,
  activeBreakpoint: WorkspaceBreakpoint
): WorkspaceLayoutState {
  const nextLayouts = {
    lg: reconcileBreakpointLayout(
      layouts.lg ? [...layouts.lg] : state.cardLayouts.lg,
      state.orderedCardIds,
      'lg'
    ),
    md: reconcileBreakpointLayout(
      layouts.md ? [...layouts.md] : state.cardLayouts.md,
      state.orderedCardIds,
      'md'
    ),
    sm: reconcileBreakpointLayout(
      layouts.sm ? [...layouts.sm] : state.cardLayouts.sm,
      state.orderedCardIds,
      'sm'
    ),
  };
  const orderedCardIds = [...nextLayouts[activeBreakpoint]]
    .sort((left, right) => left.y - right.y || left.x - right.x)
    .map((item) => item.i as WorkspaceCardId);

  return {
    ...state,
    orderedCardIds,
    pinnedCardIds: state.pinnedCardIds.filter((id) => orderedCardIds.includes(id)),
    cardLayouts: nextLayouts,
  };
}
